// operations/storage/movements/index.js
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";
import RecordsPerPage from "../../../common/RecordsPerPage";
import Pagination from "../../../common/Pagination";

const DEFAULT_RPP = 10;

const ACTION_OPTIONS = [
  { label: "All Actions", value: "" },
  { label: "Add", value: "add" },
  { label: "Remove", value: "remove" },
  { label: "Set", value: "set" },
];

const DATE_RANGE_OPTIONS = [
  { label: "All Time", value: "all" },
  { label: "Last 7 Days", value: "7" },
  { label: "Last 30 Days", value: "30" },
];

// Reuse the same locations / items you conceptually have elsewhere
const LOCATION_DUMMY = [
  { id: "loc-1", name: "Main Warehouse" },
  { id: "loc-2", name: "Driftwood Villa – Owner Closet" },
  { id: "loc-3", name: "Sand Dollar Linen Room" },
  { id: "loc-4", name: "Maintenance Garage" },
];

const ITEM_DUMMY = [
  { id: "item-1", name: "Queen Sheet Set – White", sku: "QS-WHT" },
  { id: "item-2", name: "Bath Towel – White", sku: "BT-WHT" },
  { id: "item-3", name: "Dish Sponge – 3 Pack", sku: "SPG-3PK" },
  { id: "item-4", name: "Toilet Paper – 12 Pack", sku: "TP-12" },
];

// Dummy movement log – newest first
const MOVEMENTS_DUMMY = [
  {
    id: "mv-1",
    at: "2025-11-18T10:15:00Z",
    location_id: "loc-1",
    location_name: "Main Warehouse",
    item_id: "item-1",
    item_name: "Queen Sheet Set – White",
    sku: "QS-WHT",
    action: "add",           // add | remove | set
    quantity: 12,
    resulting_on_hand: 42,
    user_name: "Ana",
    note: "Received new shipment",
  },
  {
    id: "mv-2",
    at: "2025-11-17T17:30:00Z",
    location_id: "loc-3",
    location_name: "Sand Dollar Linen Room",
    item_id: "item-2",
    item_name: "Bath Towel – White",
    sku: "BT-WHT",
    action: "remove",
    quantity: 8,
    resulting_on_hand: 6,
    user_name: "Lisa",
    note: "Pulled for 112 Sand Dollar Suite turnover",
  },
  {
    id: "mv-3",
    at: "2025-11-16T14:05:00Z",
    location_id: "loc-1",
    location_name: "Main Warehouse",
    item_id: "item-4",
    item_name: "Toilet Paper – 12 Pack",
    sku: "TP-12",
    action: "set",
    quantity: 50,
    resulting_on_hand: 50,
    user_name: "Sam",
    note: "Manual count reset after spot check",
  },
  {
    id: "mv-4",
    at: "2025-11-10T09:10:00Z",
    location_id: "loc-4",
    location_name: "Maintenance Garage",
    item_id: "item-3",
    item_name: "Dish Sponge – 3 Pack",
    sku: "SPG-3PK",
    action: "add",
    quantity: 10,
    resulting_on_hand: 10,
    user_name: "Peter",
    note: "Hardware store run",
  },
];

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const actionBadge = (action) => {
  if (action === "add") {
    return (
      <span className="uk-badge uk-background-success">Add</span>
    );
  }
  if (action === "remove") {
    return (
      <span className="uk-badge uk-background-danger">Remove</span>
    );
  }
  if (action === "set") {
    return (
      <span className="uk-badge uk-background-muted">Set</span>
    );
  }
  return <span className="uk-badge">—</span>;
};

export default function StorageMovementsIndex() {
  const [cookies] = useCookies(["records-per-page"]);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [movements] = useState(MOVEMENTS_DUMMY);
  const [criteria, setCriteria] = useState({
    search: "",
    location_id: "",
    action: "",
    date_range: "7", // default to last 7 days
    page: 0,
  });

  useEffect(() => {
    document.title = "Storage Movements - RueBaRue";
  }, []);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page:
        key === "search" ||
        key === "location_id" ||
        key === "action" ||
        key === "date_range"
          ? 0
          : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const locationOptions = useMemo(() => LOCATION_DUMMY, []);

  const filtered = useMemo(() => {
    const now = new Date();

    return movements.filter((mv) => {
      // Date window
      if (criteria.date_range !== "all") {
        const days = Number(criteria.date_range) || 0;
        if (days > 0 && mv.at) {
          const cutoff = new Date(now);
          cutoff.setDate(cutoff.getDate() - days);
          if (new Date(mv.at) < cutoff) {
            return false;
          }
        }
      }

      // Location filter
      if (criteria.location_id && mv.location_id !== criteria.location_id) {
        return false;
      }

      // Action filter
      if (criteria.action && mv.action !== criteria.action) {
        return false;
      }

      // Search
      if (criteria.search) {
        const q = criteria.search.toLowerCase();
        const hay = `${mv.item_name || ""} ${mv.sku || ""} ${
          mv.location_name || ""
        } ${mv.user_name || ""} ${mv.note || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [movements, criteria]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / recordsPerPage)
  );
  const pageRows = filtered.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage
  );

  const currentLocationLabel =
    criteria.location_id
      ? locationOptions.find((l) => l.id === criteria.location_id)?.name ||
        "All Locations"
      : "All Locations";

  const currentActionLabel =
    criteria.action
      ? ACTION_OPTIONS.find((a) => a.value === criteria.action)?.label ||
        "All Actions"
      : "All Actions";

  const currentDateRangeLabel =
    DATE_RANGE_OPTIONS.find((d) => d.value === criteria.date_range)?.label ||
    "All Time";

  return (
    <section>
      <div id="hms-page-title">
        <h1 className="uk-heading-small">Storage Movements</h1>
      </div>

      <div id="hms-main-body">
        <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
          <div className="uk-overflow-auto">
            {/* Filters */}
            <Flex
              alignment="between top"
              className="guest-form-top-filters"
            >
              <form
                className="uk-form uk-flex uk-flex-middle hms-form"
                onSubmit={(e) => e.preventDefault()}
              >
                {/* Search */}
                <div className="uk-inline uk-margin-small-right">
                  <div className="uk-search uk-search-default uk-form-custom">
                    {criteria.search ? (
                      <a
                        className="uk-form-icon-flip uk-form-icon"
                        uk-icon="icon: close"
                        onClick={() =>
                          handleCriteriaChange("search", "")
                        }
                      ></a>
                    ) : (
                      <span
                        className="uk-search-icon-flip"
                        uk-search-icon="true"
                      />
                    )}
                    <input
                      className="uk-search-input"
                      type="search"
                      placeholder="Search item, SKU, location, note"
                      value={criteria.search}
                      onChange={(e) =>
                        handleCriteriaChange("search", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Location filter */}
                <div
                  className="uk-inline uk-margin-small-right"
                  uk-form-custom="target: > * > span:first-child"
                >
                  <select
                    value={criteria.location_id}
                    onChange={(e) =>
                      handleCriteriaChange("location_id", e.target.value)
                    }
                  >
                    <option value="">All Locations</option>
                    {locationOptions.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="uk-button uk-button-default"
                    type="button"
                    tabIndex="-1"
                  >
                    <span className="select-value">
                      {currentLocationLabel}
                    </span>
                    <span
                      uk-icon="icon: chevron-down"
                      className="hms-align-right"
                    ></span>
                  </button>
                </div>

                {/* Action filter */}
                <div
                  className="uk-inline uk-margin-small-right"
                  uk-form-custom="target: > * > span:first-child"
                >
                  <select
                    value={criteria.action}
                    onChange={(e) =>
                      handleCriteriaChange("action", e.target.value)
                    }
                  >
                    {ACTION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="uk-button uk-button-default"
                    type="button"
                    tabIndex="-1"
                  >
                    <span className="select-value">
                      {currentActionLabel}
                    </span>
                    <span
                      uk-icon="icon: chevron-down"
                      className="hms-align-right"
                    ></span>
                  </button>
                </div>

                {/* Date range filter */}
                <div
                  className="uk-inline uk-margin-small-right"
                  uk-form-custom="target: > * > span:first-child"
                >
                  <select
                    value={criteria.date_range}
                    onChange={(e) =>
                      handleCriteriaChange("date_range", e.target.value)
                    }
                  >
                    {DATE_RANGE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="uk-button uk-button-default"
                    type="button"
                    tabIndex="-1"
                  >
                    <span className="select-value">
                      {currentDateRangeLabel}
                    </span>
                    <span
                      uk-icon="icon: chevron-down"
                      className="hms-align-right"
                    ></span>
                  </button>
                </div>
              </form>
            </Flex>

            {/* Table */}
            <table className="uk-table uk-table-hover uk-table-divider hms-table">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th className="uk-table-expand">Location</th>
                  <th className="uk-table-expand">Item</th>
                  <th>SKU</th>
                  <th>Action</th>
                  <th>Qty</th>
                  <th>On Hand After</th>
                  <th>User</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={9}>
                      <div className="uk-text-center uk-padding-small uk-text-meta">
                        No movements found. Adjust your filters.
                      </div>
                    </td>
                  </tr>
                )}

                {pageRows.map((mv) => (
                  <tr key={mv.id}>
                    <td>{formatDateTime(mv.at)}</td>
                    <td className="semi-bold-font">
                      {mv.location_name || "—"}
                    </td>
                    <td>{mv.item_name || "—"}</td>
                    <td>{mv.sku || "—"}</td>
                    <td>{actionBadge(mv.action)}</td>
                    <td>{mv.quantity ?? "—"}</td>
                    <td>{mv.resulting_on_hand ?? "—"}</td>
                    <td>{mv.user_name || "—"}</td>
                    <td>{mv.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Flex
              alignment="between top"
              className="guest-form-top-filters"
            >
              <Pagination
                count={totalPages}
                index={Math.min(criteria.page, totalPages - 1)}
                onIndexChanged={handleIndexChanged}
              />
              <RecordsPerPage />
            </Flex>
          </div>
        </div>
      </div>
    </section>
  );
}